// ============================================================================
// TENANTS MANAGEMENT - Platform Admin only
// Create, edit, activate/deactivate tenants
// NO seeding - packs are managed explicitly via Pack Manager
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Building, Plus, Trash2, Edit2, Check, X, Loader2,
  ChevronRight, Package, Users, AlertCircle, Rocket
} from 'lucide-react';
import {
  getTenants, saveTenant, deleteTenant, updateTenantLifecycle, getUsers,
  saveImpactSettings, DEFAULT_IMPACT_SETTINGS,
} from '../core/index.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { loadTenantConfigReadiness } from '../core/platformHealth.js';

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const TenantsView = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState({});
  const [userCounts, setUserCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [lifecycleConfirm, setLifecycleConfirm] = useState(null);
  const [lifecycleSaving, setLifecycleSaving] = useState(false);
  // Per-tenant config readiness, keyed by tenantId:
  //   { loading } | { readinessError } | { ...counts, criticalMissing, advisory, canGoLive }
  const [readinessMap, setReadinessMap] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    region: 'US-CA',
    seats: 10,
    createDefaultSettings: true,
  });

  const loadTenants = useCallback(async () => {
    try {
      const [tenantsData, usersData] = await Promise.all([
        getTenants(),
        getUsers(),
      ]);
      
      setTenants(tenantsData);
      
      // Calculate active users per tenant
      const counts = {};
      (usersData || []).forEach(user => {
        if (user.tenantId && user.status === 'active') {
          counts[user.tenantId] = (counts[user.tenantId] || 0) + 1;
        }
      });
      setUserCounts(counts);
    } catch (err) {
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const generateTenantId = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 30);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Tenant name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const tenantId = generateTenantId(formData.name);

      await saveTenant({
        tenantId,
        name: formData.name,
        region: formData.region,
        seats: formData.seats,
        status: 'active',
        counters: {},
      });

      if (formData.createDefaultSettings) {
        await saveImpactSettings(tenantId, user?.uid, {
          ...DEFAULT_IMPACT_SETTINGS.assumptions,
          completedOutcomeStatuses: DEFAULT_IMPACT_SETTINGS.definitions.completedOutcomeStatuses,
          preventedOutcomeStatuses: DEFAULT_IMPACT_SETTINGS.definitions.preventedOutcomeStatuses,
          enableTelehealthContext: DEFAULT_IMPACT_SETTINGS.segmentation.enableTelehealthContext,
          sla: DEFAULT_IMPACT_SETTINGS.sla,
          businessHours: DEFAULT_IMPACT_SETTINGS.businessHours,
        });
      }

      setShowCreate(false);
      setFormData({ name: '', region: 'US-CA', seats: 10, createDefaultSettings: true });
      loadTenants();
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editTenant) return;

    setSaving(true);
    setError('');

    try {
      await saveTenant({
        ...editTenant,
        name: formData.name,
        region: formData.region,
        seats: formData.seats,
      });

      setEditTenant(null);
      loadTenants();
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (tenant) => {
    try {
      await saveTenant({
        ...tenant,
        status: tenant.status === 'active' ? 'disabled' : 'active',
      });
      loadTenants();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  };

  const handleDelete = async (tenantId) => {
    if (!confirm(`Are you sure you want to delete "${tenantId}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteTenant(tenantId);
      loadTenants();
    } catch (err) {
      console.error('Error deleting tenant:', err);
    }
  };

  const openEdit = (tenant) => {
    setEditTenant(tenant);
    setFormData({
      name: tenant.name,
      region: tenant.region,
      seats: tenant.seats || 10,
    });
  };

  // Per-tenant config readiness (never-PHI counts) for the Mark-Live gate + badge.
  // Lightweight: enabled specialties + active members + active packs only.
  useEffect(() => {
    const entries = Object.entries(tenants || {});
    if (entries.length === 0) { setReadinessMap({}); return undefined; }
    let cancelled = false;
    const idFor = ([key, t]) => t?.tenantId || t?.id || key;
    setReadinessMap(Object.fromEntries(entries.map((e) => [idFor(e), { loading: true }])));
    entries.forEach(async (e) => {
      const id = idFor(e);
      const r = await loadTenantConfigReadiness(id);
      if (!cancelled) setReadinessMap((prev) => ({ ...prev, [id]: r }));
    });
    return () => { cancelled = true; };
  }, [tenants]);

  const handleLifecycleToggle = async () => {
    if (!lifecycleConfirm) return;
    const { tenantId, newLifecycle } = lifecycleConfirm;
    // Readiness gate — only when going LIVE. Reverting to pre_launch is always allowed.
    // The confirm button is also disabled in these cases; this is defense-in-depth
    // so a broken/unverified tenant is never written to lifecycle:'live'.
    if (newLifecycle === 'live') {
      const r = readinessMap[tenantId];
      if (!r || r.loading || r.readinessError || !r.canGoLive) return;
    }
    setLifecycleSaving(true);
    try {
      await updateTenantLifecycle(tenantId, newLifecycle);
      setLifecycleConfirm(null);
      loadTenants();
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setLifecycleSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tenantList = Object.values(tenants);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-white mb-2">Tenants</h2>
          <p className="text-slate-400">Manage healthcare organizations</p>
        </div>
        <button
          onClick={() => {
            setShowCreate(true);
            setFormData({ name: '', region: 'US-CA', seats: 10, createDefaultSettings: true });
            setError('');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white font-medium"
        >
          <Plus className="w-4 h-4" />
          New Tenant
        </button>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
        <p className="text-sm text-blue-300">
          <strong>Note:</strong> After creating a tenant, go to <strong>Pack Manager</strong> to enable specialties and upload packs for this tenant.
        </p>
      </div>

      {/* Tenants Table */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-900/50">
            <tr>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-4">Tenant</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-4">Region</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-4">Seats</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-4">Status</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-4">Lifecycle</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-4">Active Users</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {tenantList.map((tenant) => {
              const tenantId = tenant.tenantId || tenant.id;
              const activeUserCount = userCounts[tenantId] || 0;
              
              return (
                <tr key={tenantId} className="hover:bg-slate-800/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                        <Building className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{tenant.name}</p>
                        <p className="text-xs text-slate-500">{tenantId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-400">{tenant.region}</td>
                  <td className="px-6 py-4 text-sm text-slate-400">{tenant.seats || '—'}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleStatus(tenant)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        tenant.status === 'active'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-slate-700/50 text-slate-400'
                      }`}
                    >
                      {tenant.status}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const lifecycle = tenant.lifecycle || 'pre_launch';
                      const isLive = lifecycle === 'live';
                      return (
                        <button
                          onClick={() => {
                            const tenantId2 = tenant.tenantId || tenant.id;
                            setLifecycleConfirm({
                              tenantId: tenantId2,
                              tenantName: tenant.name,
                              currentLifecycle: lifecycle,
                              newLifecycle: isLive ? 'pre_launch' : 'live',
                            });
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            isLive
                              ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                              : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                          }`}
                          title={isLive ? 'Mark as pre-launch' : 'Mark as live'}
                        >
                          <Rocket className="w-3 h-3" />
                          {isLive ? 'Live' : 'Pre-launch'}
                        </button>
                      );
                    })()}
                    {(() => {
                      const tid = tenant.tenantId || tenant.id;
                      const r = readinessMap[tid];
                      let label; let cls;
                      if (!r || r.loading) { label = 'Checking…'; cls = 'text-slate-500'; }
                      else if (r.readinessError) { label = 'Readiness unavailable'; cls = 'text-slate-400'; }
                      else if (r.criticalMissing.length > 0) { label = 'Cannot go live'; cls = 'text-red-400'; }
                      else if (r.advisory.length > 0) { label = 'Needs setup'; cls = 'text-amber-400'; }
                      else { label = 'Ready'; cls = 'text-emerald-400'; }
                      return <div className={`mt-1 text-[11px] ${cls}`} title="Live readiness (config only)">{label}</div>;
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-400">{activeUserCount}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(tenant)}
                        className="p-2 rounded-lg hover:bg-blue-500/20 text-slate-500 hover:text-blue-400"
                        title="Edit tenant"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tenantId)}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-slate-500 hover:text-red-400"
                        title="Delete tenant"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {tenantList.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                  No tenants yet. Create your first tenant to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Tenant">
        <div className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Tenant Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Hopkins Hospital"
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white placeholder:text-slate-600"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Tenant ID / Slug</label>
            <input
              type="text"
              value={formData.name ? generateTenantId(formData.name) : ''}
              readOnly
              placeholder="auto-generated from name"
              className="w-full bg-slate-900/60 border border-slate-700/50 rounded-lg px-4 py-3 text-slate-400 cursor-not-allowed"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Region</label>
            <select
              value={formData.region}
              onChange={(e) => setFormData((prev) => ({ ...prev, region: e.target.value }))}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
            >
              {US_STATES.map((s) => (
                <option key={s} value={`US-${s}`}>US-{s}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-300">Seats</label>
              <span className="text-sm font-semibold text-emerald-400 tabular-nums">{formData.seats}</span>
            </div>
            <input
              type="range"
              value={formData.seats}
              onChange={(e) => setFormData((prev) => ({ ...prev, seats: parseInt(e.target.value) }))}
              min={1}
              max={500}
              step={1}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-emerald-500"
            />
            <div className="flex justify-between text-xs text-slate-600">
              <span>1</span>
              <span>500</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Lifecycle</label>
            <div className="w-full bg-slate-900/60 border border-slate-700/50 rounded-lg px-4 py-3 text-amber-400 text-sm">
              Pre-launch
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.createDefaultSettings}
              onChange={(e) => setFormData((prev) => ({ ...prev, createDefaultSettings: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500 accent-emerald-500"
            />
            <span className="text-sm text-slate-300">Create default impact/SLA settings</span>
          </label>

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setShowCreate(false)}
              className="flex-1 px-4 py-3 rounded-lg bg-slate-800/50 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="flex-1 px-4 py-3 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Create Tenant
            </button>
          </div>
        </div>
      </Modal>

      {/* Lifecycle Confirmation Modal */}
      <Modal
        isOpen={!!lifecycleConfirm}
        onClose={() => !lifecycleSaving && setLifecycleConfirm(null)}
        title={lifecycleConfirm?.newLifecycle === 'live' ? 'Mark Tenant as Live' : 'Revert to Pre-launch'}
      >
        <div className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {lifecycleConfirm?.newLifecycle === 'live' ? (
            <div className="space-y-3">
              <p className="text-slate-300">
                You are about to mark <strong className="text-white">{lifecycleConfirm?.tenantName}</strong> as <strong className="text-emerald-400">Live</strong>.
              </p>
              {(() => {
                const r = readinessMap[lifecycleConfirm.tenantId];
                if (!r || r.loading) {
                  return (
                    <div className="p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-400 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Checking readiness…
                    </div>
                  );
                }
                if (r.readinessError) {
                  return (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                      Could not verify readiness — retry.
                    </div>
                  );
                }
                const items = [
                  { ok: r.activeUserCount > 0, label: 'Add at least one active user.' },
                  { ok: r.enabledSpecialtyCount > 0, label: 'Enable at least one receiving specialty.' },
                  { ok: r.activePackCount > 0, label: 'Publish at least one active Pack.' },
                ];
                return (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg border border-slate-700/50 bg-slate-800/40 text-sm space-y-1.5">
                      {items.map((it, i) => (
                        <div key={i} className={`flex items-center gap-2 ${it.ok ? 'text-emerald-300' : 'text-red-300'}`}>
                          {it.ok ? <Check className="w-3.5 h-3.5 shrink-0" /> : <X className="w-3.5 h-3.5 shrink-0" />}
                          {it.label}
                        </div>
                      ))}
                    </div>
                    {!r.canGoLive && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
                        Tenant cannot be marked Live until these are complete.
                        <div className="text-xs text-red-300/80 mt-1">
                          Use Users to add active users. Use Pack Manager to enable specialties and publish Packs.
                        </div>
                      </div>
                    )}
                    {r.advisory.length > 0 && (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-300">
                        Advisory (non-blocking): {r.advisory.map((a) => a.label).join(' ')}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-300">
                Live tenants are monitored for activity, operational metrics, and value delivery. Risk signals will be active.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-slate-300">
                You are about to revert <strong className="text-white">{lifecycleConfirm?.tenantName}</strong> to <strong className="text-amber-400">Pre-launch</strong>.
              </p>
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-300">
                Pre-launch tenants skip activity and operational risk signals. Only configuration issues will be flagged.
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setLifecycleConfirm(null)}
              disabled={lifecycleSaving}
              className="flex-1 px-4 py-3 rounded-lg bg-slate-800/50 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleLifecycleToggle}
              disabled={lifecycleSaving || (lifecycleConfirm?.newLifecycle === 'live' && readinessMap[lifecycleConfirm.tenantId]?.canGoLive !== true)}
              className={`flex-1 px-4 py-3 rounded-lg text-white disabled:opacity-50 flex items-center justify-center gap-2 ${
                lifecycleConfirm?.newLifecycle === 'live'
                  ? 'bg-emerald-500 hover:bg-emerald-600'
                  : 'bg-amber-500 hover:bg-amber-600'
              }`}
            >
              {lifecycleSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              {lifecycleConfirm?.newLifecycle === 'live' ? 'Confirm: Mark Live' : 'Confirm: Revert'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editTenant} onClose={() => setEditTenant(null)} title="Edit Tenant">
        <div className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Tenant Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">Region</label>
            <select
              value={formData.region}
              onChange={(e) => setFormData((prev) => ({ ...prev, region: e.target.value }))}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
            >
              {US_STATES.map((s) => (
                <option key={s} value={`US-${s}`}>US-{s}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-300">Seats</label>
              <span className="text-sm font-semibold text-emerald-400 tabular-nums">{formData.seats}</span>
            </div>
            <input
              type="range"
              value={formData.seats}
              onChange={(e) => setFormData((prev) => ({ ...prev, seats: parseInt(e.target.value) }))}
              min={1}
              max={500}
              step={1}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-emerald-500"
            />
            <div className="flex justify-between text-xs text-slate-600">
              <span>1</span>
              <span>500</span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setEditTenant(null)}
              className="flex-1 px-4 py-3 rounded-lg bg-slate-800/50 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={saving}
              className="flex-1 px-4 py-3 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TenantsView;
